import React, { useState, useEffect } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CardMedia from "@mui/material/CardMedia";
import IconButton from "@mui/material/IconButton";
import Swal from "sweetalert2";
import ArchiveIcon from "@mui/icons-material/Archive";
import { getAuthCredentials } from "../../utils/auth";
import TitleSetter from "../../component/TitleSetter";

const MenuItems = ({
  menuItems,
  ingredients,
  onArchive,
  onEdit,
  onEditOptions,
  onEditTags,
}) => {
  const [menuWithImages, setMenuWithImages] = useState([]);
  const { token } = getAuthCredentials();

  useEffect(() => {
    const loadImages = async () => {
      const updatedMenuItems = await Promise.all(
        menuItems.map(async (item) => {
          const imageUrl = item.menuImage || null;
          return { ...item, image: imageUrl };
        })
      );

      const sortedMenuItems = updatedMenuItems.sort((a, b) =>
        (a.category || "").localeCompare(b.category || "")
      );

      setMenuWithImages(sortedMenuItems);
    };

    loadImages();
  }, [menuItems]);

  const findIngredient = (ingredientName) => {
    const ingredient = ingredients.find(
      (ingredient) => ingredient.inventoryName === ingredientName
    );

    if (!ingredient) return null;

    const validInstances = ingredient.instances.filter(
      (instance) =>
        instance.currentAmount > 0 && new Date(instance.expiryDate) > new Date()
    );

    const totalCurrentAmount = validInstances.reduce(
      (sum, instance) => sum + instance.currentAmount,
      0
    );

    return {
      ...ingredient,
      totalCurrentAmount,
    };
  };

  const categorizedMenuItems = menuWithImages.reduce((acc, item) => {
    let canMakeCount = Infinity;
    let lowStockIngredients = [];
    let anyIngredientMissing = false;

    item.menuIngredients?.forEach((ingredient) => {
      const detail = findIngredient(ingredient.ingredientName);
      if (!detail) {
        canMakeCount = 0;
        anyIngredientMissing = true;
      } else {
        const maxServings = Math.floor(
          detail.totalCurrentAmount / ingredient.amount
        );
        if (isNaN(maxServings) || maxServings <= 0) {
          canMakeCount = 0;
        } else {
          canMakeCount = Math.min(canMakeCount, maxServings);
          if (maxServings <= 10) {
            lowStockIngredients.push({
              ingredientName: ingredient.ingredientName,
              remainingServings: maxServings,
              requiredPerUnit: ingredient.amount,
            });
          }
        }
      }
    });

    const outOfStock = canMakeCount <= 0 || anyIngredientMissing;
    const isLowStock = !outOfStock && lowStockIngredients.length > 0;

    const category = outOfStock
      ? "Out of Stock"
      : isLowStock
      ? "Low Stock"
      : item.categoryName || "Uncategorized";

    if (!acc[category]) acc[category] = [];
    acc[category].push({ ...item, lowStockIngredients });

    return acc;
  }, {});

  const onFetchAllergens = async (menuItem) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/fetch-allergens/${menuItem._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const allergens = response.data.allergens.join(", ");

      Swal.fire({
        title: "Allergens",
        text: allergens,
        icon: "info",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Error fetching allergens:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch allergens. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const sortedCategories = Object.keys(categorizedMenuItems).sort((a, b) => {
    const priority = { "Out of Stock": 0, "Low Stock": 1 };
    return (priority[a] ?? 2) - (priority[b] ?? 2) || a.localeCompare(b);
  });

  return (
    <Box>
      <TitleSetter title="Menu Items" />  
      {sortedCategories.map((category, categoryIndex) => (
        <Box key={categoryIndex} sx={{ marginBottom: 4 }}>
          <Typography variant="h5" gutterBottom>
            {category}
          </Typography>
          <Grid container spacing={3}>
            {categorizedMenuItems[category].map((item, index) => {
              const hasMissingIngredients = item.menuIngredients?.some(
                (ingredient) => {
                  const ingredientData = findIngredient(
                    ingredient.ingredientName
                  );
                  return (
                    !ingredientData ||
                    ingredientData.totalCurrentAmount < ingredient.amount
                  );
                }
              );

              const status =
                item.options && item.options.length > 0
                  ? "Options Added"
                  : "Options Not Added";
              const statusColor =
                status === "Options Added" ? "#B0EACD" : "#FFB6C1";

              return (
                <Grid item xs={12} sm={6} key={index} sx={{ display: "flex" }}>
                  <Card
                    sx={{
                      backgroundColor: hasMissingIngredients
                        ? "#FFCCCB"
                        : "#ADD8E6",
                      position: "relative",
                      padding: 1,
                      transition: "transform 0.2s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.03)",
                      },
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                    }}
                    onClick={() => onEdit(item)}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        backgroundColor: statusColor,
                        color: "#000",
                        padding: "4px 8px",
                        fontSize: "0.9em",
                        fontWeight: "bold",
                        borderRadius: "0 0 4px 0",
                        zIndex: 2,
                      }}
                    >
                      {status}
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                      {item.image && (
                        <CardMedia
                          component="img"
                          image={item.image}
                          alt={item.name}
                          sx={{
                            width: 175,
                            height: 175,
                            objectFit: "cover",
                            borderRadius: "8px",
                            marginTop: 5,
                          }}
                        />
                      )}

                      <CardContent sx={{ flex: 1, paddingTop: 0 }}>
                        <Typography variant="h6" gutterBottom>
                          {item.menuName}
                        </Typography>

                        <Typography variant="body2" color="textSecondary">
                          Ingredients:
                        </Typography>
                        <ul style={{ marginTop: 0 }}>
                          {item.menuIngredients?.map((ingredient, i) => {
                            const detail = findIngredient(
                              ingredient.ingredientName
                            );
                            const totalAvailable =
                              detail?.totalCurrentAmount || 0;
                            const requiredPerUnit = ingredient.amount;
                            const maxServings = Math.floor(
                              totalAvailable / requiredPerUnit
                            );

                            let color = undefined;
                            let label = "";

                            if (!detail || maxServings <= 0) {
                              color = "red";
                              label = "– Out of Stock";
                            } else if (maxServings <= 10) {
                              color = "#ff8800";
                              label = `– ${maxServings} unit${
                                maxServings !== 1 ? "s" : ""
                              } remaining`;
                            }

                            return (
                              <li key={i}>
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(
                                      `/inventory?search=${encodeURIComponent(
                                        ingredient.ingredientName
                                      )}`,
                                      "_blank"
                                    );
                                  }}
                                  style={{
                                    color: color || "inherit",
                                    fontWeight: color ? "bold" : "normal",
                                    textDecoration: color
                                      ? "underline"
                                      : "none",
                                    cursor: "pointer",
                                  }}
                                  title="Click to view in Inventory"
                                >
                                  {ingredient.ingredientName} –{" "}
                                  {ingredient.amount}{" "}
                                  {detail?.unitOfMeasurement || ""} {label}
                                </span>
                              </li>
                            );
                          })}
                        </ul>

                        <Typography variant="body1">
                          Price: PHP {item.basePrice}
                        </Typography>

                        {item.menuTags?.some((tag) =>
                          tag.startsWith("spicy:")
                        ) &&
                          (() => {
                            const spicyTag = item.menuTags.find((tag) =>
                              tag.startsWith("spicy:")
                            );
                            const spicyName =
                              spicyTag?.split("spicy: ")[1] || "";
                            const chiliCount =
                              spicyName === "mild"
                                ? 1
                                : spicyName === "pinoy"
                                ? 2
                                : spicyName === "thai"
                                ? 3
                                : 0;

                            return (
                              <Typography variant="body1">
                                Spicy: {spicyName} {"🌶️".repeat(chiliCount)}
                              </Typography>
                            );
                          })()}

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            marginTop: 2,
                          }}
                        >
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditOptions(item);
                            }}
                            variant="contained"
                            color="primary"
                            size="small"
                          >
                            Options
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditTags(item);
                            }}
                            variant="contained"
                            color="secondary"
                            size="small"
                          >
                            Tags
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(item);
                            }}
                            variant="contained"
                            color="success"
                            size="small"
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              onFetchAllergens(item);
                            }}
                            variant="contained"
                            color="warning"
                            size="small"
                          >
                            Allergens
                          </Button>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              onArchive(item);
                            }}
                            color="secondary"
                            size="small"
                          >
                            <ArchiveIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default MenuItems;
